import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// --- ANIMATION VARIANTS for Framer Motion ---
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// --- AnimatedSection Wrapper ---
const AnimatedSection = ({ children, id, className = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <motion.section
            id={id}
            ref={ref}
            className={`py-24 md:py-32 overflow-hidden ${className}`}
            variants={sectionVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
            {children}
        </motion.section>
    );
};

const AnimatedText = ({ text, className, variants, ...props }) => {
    const words = text.split(" ");
    return (
        <motion.p className={className} variants={variants} {...props}>
            {words.map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.25em]" variants={itemVariants}>
                    {word}
                </motion.span>
            ))}
        </motion.p>
    );
};

// --- FAQ SECTION ---
const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <motion.div className="border-b border-gray-200 dark:border-gray-700 py-6" variants={itemVariants}>
            <button
                className="w-full flex justify-between items-center text-left text-xl font-semibold"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-black dark:text-white">{question}</span>
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }} className="text-gray-600 dark:text-gray-400">
                    <ChevronDown />
                </motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: '16px' }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        className="text-gray-600 dark:text-gray-400 leading-relaxed"
                    >
                        {answer}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const FAQSection = () => {
    const faqs = [
        { question: "What kind of industries do you specialize in?", answer: "We work across diverse industries including FinTech, e-commerce, travel & tourism, fashion, healthcare, and startups. Our expertise spans web & app engineering, AI automation, digital marketing, and IoT solutions. From building MVP platforms for startups to implementing smart technologies for established businesses, we adapt our technical capabilities to solve unique challenges in any sector." },
        { question: "What is the typical timeline for a project?", answer: "Project timelines depend on scope and complexity. A responsive website with CMS takes 4-6 weeks, custom web applications need 8-12 weeks, while AI automation projects or e-commerce platforms can range from 12-20 weeks. We provide detailed project roadmaps during our discovery phase, with regular milestones and transparent progress tracking throughout development." },
        { question: "How do you handle project management and communication?", answer: "We use agile methodologies with transparent communication at every step. You'll have a dedicated project manager, weekly progress updates, and access to our project dashboard. We communicate via your preferred channels (Slack, email, video calls) and provide regular demos. Our iterative approach ensures you're involved in decision-making and can provide feedback throughout the development process." },
        { question: "Do you offer support and maintenance after a project is launched?", answer: "Absolutely! All our packages include initial support periods (1-6 months depending on the service). We offer ongoing maintenance plans covering security updates, performance optimization, feature enhancements, and technical support. For AI systems and e-commerce platforms, we provide specialized monitoring and optimization services to ensure peak performance." },
        { question: "What makes Smart Solutions different from other digital agencies?", answer: "We're digital architects who combine creative design with robust engineering. Our team includes specialists in AI/ML, IoT, and modern web technologies. We don't just build websites - we create comprehensive digital ecosystems including automation workflows, smart integrations, and data-driven solutions. Every project is approached strategically to ensure scalability and future-readiness." }
    ];

    return (
        <AnimatedSection id="faq" className="bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-white">
            <div className="container mx-auto px-6 max-w-4xl">
                <div className="text-center mb-16">
                    <AnimatedText text="Frequently Asked Questions" className="text-4xl md:text-6xl font-bold" variants={sectionVariants}/>
                </div>
                <div>
                    {faqs.map((faq, index) => (
                        <FAQItem key={index} question={faq.question} answer={faq.answer} />
                    ))}
                </div>
            </div>
        </AnimatedSection>
    );
};

export default FAQSection;
